import Phaser from 'phaser';

/**
 * A masked, scrollable region with a real draggable scrollbar.
 *
 * Every other list in the game steps with arrow buttons (the episode rows, the
 * hub chapters). The leaderboard is fifty rows and the avatar picker is
 * forty-seven icons: both are long enough that stepping is the wrong gesture,
 * and both were specified with a bar. Written once and shared rather than
 * twice and divergent.
 *
 * Three ways to move, because the game is played on desktops and tablets:
 * dragging the bar, the mouse wheel over the viewport, and dragging the
 * content itself. The last one is why `dragged` exists — an item inside the
 * list must not treat the end of a scroll flick as a tap on itself.
 *
 * Spec: docs/superpowers/specs/2026-09-20-leaderboards-design.md §5
 */

export interface ScrollListOptions {
  /** Viewport top-left, in scene coordinates. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Centre x of the bar. Defaults to just right of the viewport. */
  barX?: number;
  barWidth?: number;
  /** Depth for the bar and the drag catcher. */
  depth?: number;
  /**
   * Dragging the content itself scrolls (default true). Off where the items
   * are dragged somewhere else — the Equip tray's drag-and-drop — so only
   * the wheel and the bar move it.
   */
  contentDrag?: boolean;
  /**
   * Hide every Text in `content` that is not wholly inside the viewport (off
   * by default). The mask clips what the eye sees, but a clipped label is
   * still "visible" to the layout self-test, which then finds it overlapping
   * the heading above the list.
   */
  cullText?: boolean;
}

/** Below this much movement a press is a tap, not a flick. */
const DRAG_SLOP = 8;
const WHEEL_STEP = 0.6;

export class ScrollList {
  /** Put row/icon objects in here, positioned from y=0 downwards. */
  readonly content: Phaser.GameObjects.Container;

  private readonly scene: Phaser.Scene;
  private readonly opts: Required<ScrollListOptions>;
  private readonly maskShape: Phaser.GameObjects.Graphics;
  private readonly track: Phaser.GameObjects.Rectangle;
  private readonly thumb: Phaser.GameObjects.Rectangle;
  private readonly catcher: Phaser.GameObjects.Rectangle;

  private contentHeight = 0;
  private offset = 0;
  /** True from the moment a press passes the slop until the next press. */
  private draggedFar = false;
  private grabbedThumb = false;
  private grabbedContent = false;
  private grabY = 0;
  private grabOffset = 0;

  private readonly onPointerMove: (p: Phaser.Input.Pointer) => void;
  private readonly onPointerUp: () => void;
  private readonly onWheel: (
    p: Phaser.Input.Pointer,
    over: unknown,
    dx: number,
    dy: number,
  ) => void;

  constructor(scene: Phaser.Scene, options: ScrollListOptions) {
    this.scene = scene;
    const barWidth = options.barWidth ?? 10;
    this.opts = {
      ...options,
      barWidth,
      barX: options.barX ?? options.x + options.width + 16,
      depth: options.depth ?? 0,
      contentDrag: options.contentDrag ?? true,
      cullText: options.cullText ?? false,
    };
    const { x, y, width, height, barX, depth } = this.opts;

    // Catcher sits UNDER the content so dragging empty space scrolls, while
    // an icon on top still receives its own pointer events.
    this.catcher = scene.add
      .rectangle(x + width / 2, y + height / 2, width, height, 0x000000, 0)
      .setDepth(depth);
    // Without content drag there is nothing for it to catch, and an
    // interactive catcher would sit in the way of whatever the scene does.
    if (this.opts.contentDrag) this.catcher.setInteractive({ useHandCursor: false });

    this.content = scene.add.container(x, y).setDepth(depth + 1);

    this.maskShape = scene.make.graphics({});
    this.maskShape.fillStyle(0xffffff);
    this.maskShape.fillRect(x, y, width, height);
    this.content.setMask(this.maskShape.createGeometryMask());

    this.track = scene.add
      .rectangle(barX, y + height / 2, barWidth, height, 0x15131f, 0.85)
      .setDepth(depth + 2)
      .setStrokeStyle(1, 0x3a3f47);
    this.thumb = scene.add
      .rectangle(barX, y, barWidth - 2, 40, 0x8ea87a, 1)
      .setDepth(depth + 3)
      .setInteractive({ useHandCursor: true });

    this.catcher.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      this.grabbedContent = true;
      this.draggedFar = false;
      this.grabY = p.y;
      this.grabOffset = this.offset;
    });
    this.thumb.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      this.grabbedThumb = true;
      this.draggedFar = false;
      this.grabY = p.y;
      this.grabOffset = this.offset;
    });

    this.onPointerMove = (p) => {
      if (!this.grabbedThumb && !this.grabbedContent) return;
      const dy = p.y - this.grabY;
      if (Math.abs(dy) > DRAG_SLOP) this.draggedFar = true;
      if (this.grabbedThumb) {
        // Bar travel maps to content travel by the ratio of the two ranges.
        const travel = Math.max(1, height - this.thumbHeight());
        this.setOffset(this.grabOffset + (dy / travel) * this.maxOffset());
      } else {
        // Content drag follows the finger: down means "pull the list down".
        this.setOffset(this.grabOffset - dy);
      }
    };
    this.onPointerUp = () => {
      this.grabbedThumb = false;
      this.grabbedContent = false;
    };
    this.onWheel = (p, _over, _dx, dy) => {
      if (p.x < x || p.x > x + width || p.y < y || p.y > y + height) return;
      this.setOffset(this.offset + dy * WHEEL_STEP);
    };

    scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove);
    scene.input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp);
    scene.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onPointerUp);
    scene.input.on(Phaser.Input.Events.POINTER_WHEEL, this.onWheel);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());

    this.setContentHeight(0);
  }

  /**
   * Did the press that is ending travel far enough to be a scroll? Items inside
   * the list check this before acting, so a flick never equips an avatar.
   */
  get dragged(): boolean {
    return this.draggedFar;
  }

  /**
   * Is this pointer over the visible viewport? The mask only hides what is
   * scrolled out — those objects still take input — so an item inside the
   * list ignores a press that lands outside the viewport.
   */
  inView(p: { x: number; y: number }): boolean {
    const { x, y, width, height } = this.opts;
    return p.x >= x && p.x <= x + width && p.y >= y && p.y <= y + height;
  }

  /** How far the content is scrolled, in pixels from the top. */
  get scrollY(): number {
    return this.offset;
  }

  /** Call after (re)filling `content`. Keeps the view in range and redraws the bar. */
  setContentHeight(h: number): void {
    this.contentHeight = Math.max(0, h);
    this.setOffset(this.offset);
    this.layoutBar();
  }

  /** Back to the top — used when the board switches to another tab. */
  scrollToTop(): void {
    this.setOffset(0);
  }

  private maxOffset(): number {
    return Math.max(0, this.contentHeight - this.opts.height);
  }

  private thumbHeight(): number {
    const { height } = this.opts;
    if (this.contentHeight <= height) return height;
    return Phaser.Math.Clamp((height / this.contentHeight) * height, 28, height);
  }

  private setOffset(next: number): void {
    this.offset = Phaser.Math.Clamp(next, 0, this.maxOffset());
    this.content.y = this.opts.y - this.offset;
    this.layoutBar();
    if (this.opts.cullText) this.cull();
  }

  /** See `cullText`. Children are positioned from y=0, so offset maps them. */
  private cull(): void {
    const { height } = this.opts;
    for (const o of this.content.list) {
      if (!(o instanceof Phaser.GameObjects.Text)) continue;
      const top = o.y - o.displayHeight * o.originY - this.offset;
      o.setVisible(top >= 0 && top + o.displayHeight <= height);
    }
  }

  private layoutBar(): void {
    const { y, height } = this.opts;
    const scrollable = this.maxOffset() > 0;
    // A bar that cannot move is noise: hide it rather than draw a full-height
    // thumb the player will try to drag.
    this.track.setVisible(scrollable);
    this.thumb.setVisible(scrollable);
    if (!scrollable) return;
    const th = this.thumbHeight();
    const travel = Math.max(1, height - th);
    const frac = this.offset / this.maxOffset();
    this.thumb.setSize(this.opts.barWidth - 2, th);
    this.thumb.setPosition(this.opts.barX, y + th / 2 + frac * travel);
    // The hit area does not follow setSize on its own.
    this.thumb.setInteractive({ useHandCursor: true });
  }

  destroy(): void {
    this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove);
    this.scene.input.off(Phaser.Input.Events.POINTER_UP, this.onPointerUp);
    this.scene.input.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onPointerUp);
    this.scene.input.off(Phaser.Input.Events.POINTER_WHEEL, this.onWheel);
    this.content.clearMask(true);
    this.maskShape.destroy();
    this.catcher.destroy();
    this.track.destroy();
    this.thumb.destroy();
    this.content.destroy();
  }
}
